import { useHostElement } from "#ce/composables/useHostElement";

/**
 * Expose properties and methods to the host element.
 * Properties and methods are applied immediately since the host is available synchronously.
 *
 * @returns {Object}
 * @property {Function} exposeProperties
 * @property {Function} exposeMethods
 */
export function useExpose() {
  const host = useHostElement();

  function exposeProperties(properties) {
    if (!host) {
      return;
    }

    for (const [key, descriptor] of Object.entries(properties)) {
      // Capture any value that was set on the element before the accessor is installed.
      // This happens when external code (e.g. jQuery.val()) sets a plain data property
      // before the Vue component has had a chance to define the accessor.
      const existingValue = host[key];
      Reflect.defineProperty(host, key, descriptor);

      if (existingValue !== undefined && descriptor.set) {
        descriptor.set(existingValue);
      }
    }
  }

  function exposeMethods(methods) {
    if (!host) {
      return;
    }
    Object.assign(host, methods);
  }

  return {
    /**
     * Expose properties to the host element.
     *
     * @param {Object} properties
     * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/defineProperties
     *
     * @example
     * exposeProperties({
     *  property1: {
     *   value: "value1",
     *  },
     *  property2: {
     *   get: () => "value2",
     *   set: (value) => [...]
     *  },
     * });
     */
    exposeProperties,

    /**
     * Expose methods to the host element.
     *
     * @param {Object} methods
     * @see https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Object/assign
     *
     * @example
     * exposeMethods({
     *  method1() {
     *   console.log("method1");
     *  },
     *  method2() {
     *   console.log("method2");
     *  },
     * });
     */
    exposeMethods,
  };
}
